<script lang="ts" setup>
import type { StaticPagesListItem } from '~/types/StaticPageList';

const {
  item,
  isShowAnnotation = true,
  variant = 'default',
} = defineProps<{
  item: StaticPagesListItem;
  isShowAnnotation?: boolean;
  variant?: 'default' | 'blog';
}>();

const dayjs = useDayjs();
const formattedDate = dayjs(item.publication_date).format('DD MMMM YYYY');
</script>

<template>
  <div
    :class="{ 'static-page-list-item--blog': variant === 'blog' }"
    class="static-page-list-item"
  >
    <picture
      v-if="item.image?.url"
      class="static-page-list-item__image"
    >
      <source
        :srcset="item.image?.conversions_webp.thumb_390"
        type="image/webp"
      />

      <source
        :srcset="item.image?.conversions.thumb_390"
        type="image/jpeg"
      />

      <img
        :alt="item.image?.properties?.title"
        :src="item.image?.url"
        class="size-full"
        loading="lazy"
      />
    </picture>

    <div
      v-else
      class="static-page-list-item__no-image"
    />

    <div class="static-page-list-item__info">
      <div
        :class="{ 'line-clamp-2': variant === 'blog' }"
        class="font-bold"
      >
        {{ item.name }}
      </div>

      <div
        v-if="variant !== 'blog' && isShowAnnotation"
        class="flex-grow text-black-80"
      >
        {{ item.annotation }}
      </div>

      <div
        v-if="variant !== 'blog'"
        class="flex justify-between items-end"
      >
        <div class="text-black-60 text-xs">{{ formattedDate }}</div>

        <CountdownTimer :date="item.published_till" />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.static-page-list-item {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  width: 100%;
  height: 100%;
  border-radius: 16px;
  background-color: $white;
  box-shadow: 0 0 16px rgb(0 0 0 / 5%);
  transition: box-shadow 0.3s;

  &__image {
    aspect-ratio: 16/9;

    img {
      object-fit: contain;
    }
  }

  &__no-image {
    width: 100%;
    background:
      linear-gradient(217deg, rgba($black, 0.8), rgba($black, 0) 70.71%),
      linear-gradient(127deg, rgba($black-60, 0.8), rgba($black-60, 0) 70.71%),
      linear-gradient(336deg, rgba($black, 0.8), rgba($black, 0) 70.71%);
    aspect-ratio: 16/9;
  }

  &__info {
    display: flex;
    flex-grow: 1;
    flex-direction: column;
    gap: 16px;
    justify-content: space-between;
    padding: 20px;
    font-size: 14px;
  }

  &--blog {
    height: auto;
    aspect-ratio: 1 / 1;

    .static-page-list-item__image,
    .static-page-list-item__no-image {
      display: block;
      flex-shrink: 0;
      width: 100%;
      aspect-ratio: 3 / 2;
    }

    .static-page-list-item__info {
      padding: 16px;
    }

    .static-page-list-item__image img {
      object-fit: cover;
    }
  }
}
</style>
